import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Menu,
  MenuDivider,
  MenuGroup,
  MenuGroupHeader,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  makeStyles,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import {
  ChevronDownRegular,
  OrganizationRegular,
  PersonRegular,
  QuestionCircleRegular,
  SignOutRegular,
  WrenchScrewdriverRegular,
} from "@fluentui/react-icons";
import React, { useState } from "react";
import { useNavigate } from "react-router";

import { checkIsMfaSettingRequired } from "@/helpers/checkIsMfaSettingRequired";
import { openHelpPage } from "@/helpers/openHelpPage";
import { translation } from "@/i18n";
import { nsAccountMenu } from "@/i18n/locales/accountMenu";
import { nsOrg } from "@/i18n/locales/org";
import optiumLogo from "@/images/optium_logo.svg";
import { useDialogStyles } from "@/styles";

import { AccountPersona } from "../AccountPersona";
import { useAppAccount } from "../AppProvider/AppAccountProvider";
import { useAppOrgsAndInvitedOrgs } from "../AppProvider/AppOrgsAndInvitedOrgsProvider";
import { LanguageSelectAccountMenuSubmenu } from "../LanguageSelect/LanguageSelectAccountMenuSubmenu";
import { LoadingCover } from "../LoadingCover";
import { SignOutConfirmDialog } from "../SignOutConfirmDialog";

const ICON_COLOR = "var(--figma-color-lightBlack)";

const useStyles = makeStyles({
  trigger: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap("4px"),
    ...shorthands.padding("0", "4px"),
    ...shorthands.border("none"),
    height: "45px",
    backgroundColor: "transparent",
    cursor: "pointer",
    color: tokens.colorNeutralForeground2,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  popover: {
    minWidth: "260px",
    maxWidth: "300px",
    maxHeight: "calc(100dvh - 57px)",
    overflowY: "auto",
  },
  menuItem: {
    paddingLeft: "8px",
    gap: "8px",
  },
  persona: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap("8px"),
    ...shorthands.padding("6px", "4px"),
  },
  personaTexts: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  personaName: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  personaEmail: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  footer: {
    // MenuPopover の padding (4px) を相殺してフッター帯を端まで広げる
    marginTop: "4px",
    marginRight: "-4px",
    marginBottom: "-4px",
    marginLeft: "-4px",
    ...shorthands.padding("10px", "14px"),
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderTop("1px", "solid", tokens.colorNeutralStroke2),
    borderBottomLeftRadius: tokens.borderRadiusMedium,
    borderBottomRightRadius: tokens.borderRadiusMedium,
    display: "flex",
  },
  footerButton: {
    ...shorthands.padding("0"),
    ...shorthands.border("none"),
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    textDecorationLine: "underline",
    cursor: "pointer",
    ":disabled": {
      cursor: "not-allowed",
      color: tokens.colorNeutralForegroundDisabled,
    },
  },
  logo: {
    width: "240px",
    marginTop: "20px",
    marginBottom: "20px",
  },
  dialogContentWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: "20px",
  },
});

const useTranslation = translation.use(nsOrg, nsAccountMenu);

/** ヘッダー右上のアカウントメニュー (アカウント設定・参加組織・言語・ヘルプ・ログアウト・管理者・当サービスについて)。 */
export const AccountMenu: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dialogClasses = useDialogStyles();
  const styles = useStyles();

  const { account, isAAAdminOrAbove, isAADeveloperOrAbove } = useAppAccount();
  const { orgServiceType } = useAppOrgsAndInvitedOrgs();

  const isMfaSettingRequired = checkIsMfaSettingRequired(account);
  const showAdminSection = isAAAdminOrAbove || isAADeveloperOrAbove;

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSignOutDialogVisible, setIsSignOutDialogVisible] = useState(false);
  const [isReleaseVersionVisible, setIsReleaseVersionVisible] = useState(false);

  const closeMenu = () => setIsMenuOpen(false);

  if (!account) {
    return <LoadingCover />;
  }

  return (
    <>
      <Menu
        open={isMenuOpen}
        onOpenChange={(_e, data) => setIsMenuOpen(data.open)}
        positioning={{
          position: "below",
          align: "end",
          offset: { mainAxis: 5, crossAxis: 5 },
        }}
      >
        <MenuTrigger disableButtonEnhancement>
          <button
            type="button"
            className={styles.trigger}
            aria-label={t.accountMenu.trigger.ariaLabel()}
          >
            <AccountPersona account={account} size={32} />
            <ChevronDownRegular />
          </button>
        </MenuTrigger>
        <MenuPopover className={styles.popover}>
          <div className={styles.persona}>
            <AccountPersona account={account} size={28} />
            <div className={styles.personaTexts}>
              <span className={styles.personaName}>{account.display_name}</span>
              <span className={styles.personaEmail}>{account.email}</span>
            </div>
          </div>

          <MenuList>
            <MenuItem
              className={styles.menuItem}
              icon={<PersonRegular color={ICON_COLOR} />}
              disabled={isMfaSettingRequired}
              onClick={() => navigate("/account_setting")}
            >
              {t.accountMenu.item.accountSetting()}
            </MenuItem>
            <MenuItem
              className={styles.menuItem}
              icon={<OrganizationRegular color={ICON_COLOR} />}
              disabled={isMfaSettingRequired}
              onClick={() => navigate("/organizations")}
            >
              {t.org.list.title()}
            </MenuItem>
            <LanguageSelectAccountMenuSubmenu className={styles.menuItem} />
            <MenuItem
              className={styles.menuItem}
              icon={<QuestionCircleRegular color={ICON_COLOR} />}
              onClick={() => openHelpPage(orgServiceType)}
            >
              {t.accountMenu.item.help()}
            </MenuItem>
            <MenuItem
              className={styles.menuItem}
              icon={<SignOutRegular color={ICON_COLOR} />}
              onClick={() => {
                closeMenu();
                setIsSignOutDialogVisible(true);
              }}
            >
              {t.accountMenu.item.signOut()}
            </MenuItem>

            {showAdminSection && (
              <>
                <MenuDivider />
                <MenuGroup>
                  <MenuGroupHeader>
                    {t.accountMenu.adminGroup.header()}
                  </MenuGroupHeader>
                  <MenuItem
                    className={styles.menuItem}
                    icon={<WrenchScrewdriverRegular color={ICON_COLOR} />}
                    onClick={() =>
                      navigate(
                        isAAAdminOrAbove
                          ? "/admin/organizations"
                          : "/admin/accounts"
                      )
                    }
                  >
                    {t.accountMenu.adminGroup.adminPage()}
                  </MenuItem>
                </MenuGroup>
              </>
            )}
          </MenuList>
          <div className={styles.footer}>
            <button
              type="button"
              className={styles.footerButton}
              disabled={isMfaSettingRequired}
              onClick={() => {
                closeMenu();
                setIsReleaseVersionVisible(true);
              }}
            >
              {t.accountMenu.footer.about()}
            </button>
          </div>
        </MenuPopover>
      </Menu>

      <SignOutConfirmDialog
        open={isSignOutDialogVisible}
        onClose={() => setIsSignOutDialogVisible(false)}
      />

      <Dialog
        open={isReleaseVersionVisible}
        onOpenChange={() => setIsReleaseVersionVisible(false)}
      >
        <DialogSurface className={dialogClasses.dialog}>
          <DialogBody>
            <DialogTitle>{t.accountMenu.aboutDialog.title()}</DialogTitle>
            <DialogContent className={styles.dialogContentWrapper}>
              <img src={optiumLogo} alt="OPTIUM" className={styles.logo} />
              <div>
                {t.accountMenu.aboutDialog.version({
                  version: process.env.RELEASE_VERSION ?? "",
                })}
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setIsReleaseVersionVisible(false)}>
                {t.accountMenu.aboutDialog.closeButton()}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
};
